/**
 * @component FlowApprovalBanner
 * @description #1279 (P1c) — FlowAI Vorschlags- & Approval-Panel im Case-Detail.
 *   Startet einen Vorschlags-Lauf ("Lass FlowAI übernehmen") und zeigt die
 *   geordnete Aktions-Kette. Pro Aktion: Typ, Inhalt/Entwurf, Status sowie
 *   Freigeben / Ablehnen / Bearbeiten. Nachfolge-Aktionen werden erst
 *   freigebbar, wenn ihr Vorgänger ausgeführt ist (depends_on).
 *
 *   In P1c ist die Ausführung "scharf": Freigeben einer Aktion führt sie
 *   tatsächlich aus (Telegram / Kundenantwort via OMS-message_post bzw. Mail /
 *   Case schließen). Routing nach Case-Herkunft passiert im Backend.
 *
 * @prop {number|null} caseId      lokale cases.id (oder null bei reinem OMS-Case)
 * @prop {number|null} omsCaseId   OMS project.task ID (bei is_oms)
 * @prop {boolean}     isOms       true wenn reiner OMS-Case
 * @prop {function}    t           i18n-Übersetzer (mit Default-Fallback)
 *
 * @since v2.484.0 (Banner) · überarbeitet v2.612.0 (#1279)
 */
import { useEffect, useState, useCallback } from 'react';

const API = import.meta.env.VITE_API_URL || '';

const ACTION_LABELS = {
  telegram_notify_ma: ['cases.flowProposal.typeTelegram', 'Telegram an Mitarbeiter'],
  draft_customer_reply: ['cases.flowProposal.typeReply', 'Kundenantwort'],
  close_case: ['cases.flowProposal.typeClose', 'Case schließen'],
  send_customer_email: ['cases.flowProposal.typeEmail', 'Kunden-E-Mail'],
};

export default function FlowApprovalBanner({ caseId, omsCaseId = null, isOms = false, t }) {
  const tr = t || ((_k, d) => d);
  const [actions, setActions] = useState([]);
  // #1287 terminaler Ausgang des letzten Laufs (no_action/error/completed) + Begründung.
  const [outcome, setOutcome] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editVals, setEditVals] = useState({});
  // #1280 globale Sicherheits-Schalter (Default: beide AN = maximal sicher).
  const [safety, setSafety] = useState({ dry_run: true, require_double_confirm: true });

  const refId = isOms ? omsCaseId : caseId;
  const query = isOms ? `oms_case_id=${omsCaseId}` : `case_id=${caseId}`;

  const load = useCallback(async () => {
    if (!refId) return;
    try {
      const r = await fetch(`${API}/api/flowai/case-proposal?${query}`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json().catch(() => ({}));
      setActions(Array.isArray(d?.actions) ? d.actions : []);
      setOutcome(d?.outcome || null);
      setSummary(d?.terminalSummary || null);
    } catch { /* ignore */ }
  }, [refId, query]);

  const loadSafety = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/flowai/safety-switches`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json().catch(() => ({}));
      if (d?.ok) setSafety({ dry_run: d.dry_run !== false, require_double_confirm: d.require_double_confirm !== false });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSafety(); }, [loadSafety]);

  // #1280 2.-Bestätigung: bei aktivem Schalter „Wirklich senden?" abfragen.
  // Bei Dry-Run AN wird ohnehin nichts gesendet → keine Bestätigung nötig.
  function confirmSend() {
    if (safety.dry_run) return true;
    if (!safety.require_double_confirm) return true;
    return window.confirm(tr('cases.flowProposal.confirmSend', 'Diese Aktion wird jetzt WIRKLICH ausgeführt (Mail/Telegram/Case-Änderung). Wirklich senden?'));
  }

  async function startProposal() {
    setStarting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/flowai/case-proposal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isOms ? { oms_case_id: omsCaseId } : { case_id: caseId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.ok === false) { setError(d?.error || `HTTP ${r.status}`); }
      else {
        // #1287 Ausgang sofort aus der POST-Antwort übernehmen (auch no_action),
        // dann frisch nachladen (Quelle of truth nach Reload identisch).
        setOutcome(d?.outcome || null);
        setSummary(d?.terminalSummary || null);
        await load();
      }
    } catch (e) { setError(e.message); }
    finally { setStarting(false); }
  }

  async function approve(id) {
    if (!confirmSend()) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/flowai/actions/${id}/approve`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (d?.ok === false) setError(d?.error || tr('cases.flowProposal.execFailed', 'Ausführung fehlgeschlagen'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); await load(); }
  }

  // #1280 — bereits freigegebene, aber (wegen Dry-Run) nicht ausgeführte Aktion
  // jetzt scharf ausführen. Greift der 2.-Bestätigungs-Dialog.
  async function executeNow(id) {
    if (!confirmSend()) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/flowai/actions/${id}/execute`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (d?.ok === false) setError(d?.error || tr('cases.flowProposal.execFailed', 'Ausführung fehlgeschlagen'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); await load(); }
  }

  async function reject(id) {
    const reason = prompt(tr('cases.flowProposal.rejectReason', 'Ablehnungsgrund?'));
    if (!reason) return;
    setBusy(true); setError(null);
    try {
      await fetch(`${API}/api/flowai/actions/${id}/reject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); await load(); }
  }

  function startEdit(a) {
    setEditId(a.id);
    const p = a.action_payload || {};
    setEditVals({ subject: p.subject || '', body: p.body || '', message: p.message || '', reason: p.reason || '' });
  }

  async function saveEdit(a) {
    if (!confirmSend()) return;
    setBusy(true); setError(null);
    const p = {};
    if (a.action_type === 'draft_customer_reply' || a.action_type === 'send_customer_email') {
      p.subject = editVals.subject; p.body = editVals.body;
    } else if (a.action_type === 'telegram_notify_ma') {
      p.message = editVals.message;
    } else if (a.action_type === 'close_case') {
      p.reason = editVals.reason;
    }
    try {
      const r = await fetch(`${API}/api/flowai/actions/${a.id}/edit-approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: p }),
      });
      const d = await r.json().catch(() => ({}));
      if (d?.ok === false) setError(d?.error || tr('cases.flowProposal.execFailed', 'Ausführung fehlgeschlagen'));
      else setEditId(null);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); await load(); }
  }

  if (!refId) return null;

  const execById = Object.fromEntries(actions.map((a) => [a.id, a.execution_status]));
  const isExecuted = (a) => a.execution_status === 'success' || a.execution_status === 'sandbox_redirected';
  const depReady = (a) => !a.depends_on_action_id || execById[a.depends_on_action_id] === 'success';
  const isPending = (a) => a.approval_status === 'pending' || a.approval_status == null;
  // #1280 freigegeben, aber (noch) nicht ausgeführt — z.B. unter Dry-Run AN.
  const isApprovedNotExecuted = (a) =>
    (a.approval_status === 'approved' || a.approval_status === 'edited') && !isExecuted(a) && a.approval_status !== 'rejected';

  const statusBadge = (a) => {
    const ex = a.execution_status;
    if (ex === 'success') return ['✅ ' + tr('cases.flowProposal.executed', 'ausgeführt'), 'bg-green-100 text-green-800'];
    if (ex === 'failed') return ['⚠️ ' + tr('cases.flowProposal.failed', 'fehlgeschlagen'), 'bg-red-100 text-red-800'];
    if (ex === 'skipped_duplicate') return ['⏭️ ' + tr('cases.flowProposal.dupSkipped', 'Duplikat übersprungen'), 'bg-gray-100 text-gray-700'];
    if (ex === 'sandbox_redirected') return ['📦 Sandbox', 'bg-blue-100 text-blue-800'];
    if (ex === 'dry_run_blocked') return ['🧪 ' + tr('cases.flowProposal.dryRunBlocked', 'Testmodus — nicht ausgeführt'), 'bg-indigo-100 text-indigo-800'];
    if (a.approval_status === 'rejected') return ['❌ ' + tr('cases.flowProposal.rejected', 'abgelehnt'), 'bg-red-100 text-red-800'];
    if (a.approval_status === 'approved' || a.approval_status === 'edited') return ['⏳ ' + tr('cases.flowProposal.approvedPending', 'freigegeben'), 'bg-amber-100 text-amber-800'];
    return ['🕒 ' + tr('cases.flowProposal.pending', 'offen'), 'bg-amber-100 text-amber-800'];
  };

  const renderContent = (a) => {
    const p = a.action_payload || {};
    if (a.action_type === 'telegram_notify_ma') {
      return (
        <div className="text-[12px]">
          <div className="text-[11px] text-amber-900">{tr('cases.flowProposal.recipient', 'Empfänger')}: <b>{p.name || (p.user_id ? `User #${p.user_id}` : '—')}</b></div>
          <div className="mt-1 bg-white border border-amber-200 rounded p-2 whitespace-pre-wrap max-h-[180px] overflow-y-auto">{p.message || '—'}</div>
        </div>
      );
    }
    if (a.action_type === 'draft_customer_reply' || a.action_type === 'send_customer_email') {
      return (
        <div className="text-[12px]">
          {(p.subject) && <div className="text-[11px] text-amber-900">{tr('cases.flowProposal.subject', 'Betreff')}: <b>{p.subject}</b></div>}
          <div className="mt-1 bg-white border border-amber-200 rounded p-2 whitespace-pre-wrap max-h-[220px] overflow-y-auto">{p.body || p.body_md || '—'}</div>
        </div>
      );
    }
    if (a.action_type === 'close_case') {
      return <div className="text-[12px] bg-white border border-amber-200 rounded p-2 whitespace-pre-wrap">{p.reason || tr('cases.flowProposal.noReason', '(keine Begründung)')}</div>;
    }
    return <div className="text-[12px] text-amber-900">{a.action_type}</div>;
  };

  const renderEdit = (a) => {
    if (a.action_type === 'draft_customer_reply' || a.action_type === 'send_customer_email') {
      return (
        <div className="space-y-2">
          <input className="w-full border border-amber-300 rounded px-2 py-1 text-[12px]" value={editVals.subject} onChange={(e) => setEditVals({ ...editVals, subject: e.target.value })} placeholder={tr('cases.flowProposal.subject', 'Betreff')} />
          <textarea className="w-full border border-amber-300 rounded px-2 py-1 text-[12px] h-32" value={editVals.body} onChange={(e) => setEditVals({ ...editVals, body: e.target.value })} />
        </div>
      );
    }
    if (a.action_type === 'telegram_notify_ma') {
      return <textarea className="w-full border border-amber-300 rounded px-2 py-1 text-[12px] h-24" value={editVals.message} onChange={(e) => setEditVals({ ...editVals, message: e.target.value })} />;
    }
    if (a.action_type === 'close_case') {
      return <input className="w-full border border-amber-300 rounded px-2 py-1 text-[12px]" value={editVals.reason} onChange={(e) => setEditVals({ ...editVals, reason: e.target.value })} placeholder={tr('cases.flowProposal.reason', 'Begründung')} />;
    }
    return null;
  };

  return (
    <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-amber-100 border-b border-amber-200 text-[12px] font-medium text-amber-900 flex items-center gap-2">
        <span>🤖 FlowAI</span>
        {actions.length > 0
          ? <span>{tr('cases.flowProposal.title', 'Vorschlags-Kette')} ({actions.length})</span>
          : outcome === 'no_action'
            ? <span>{tr('cases.flowProposal.noAction', 'Kein Handlungsbedarf')}</span>
            : outcome === 'error'
              ? <span>{tr('cases.flowProposal.errorTitle', 'FlowAI konnte nicht abschließen')}</span>
              : <span>{tr('cases.flowProposal.empty', 'Noch kein Vorschlag für diesen Case')}</span>}
        <button
          onClick={startProposal}
          disabled={starting}
          className="ml-auto px-3 py-1 bg-[#338edc] text-white rounded text-[11px] font-medium disabled:opacity-50"
        >
          {starting ? tr('cases.flowProposal.starting', 'FlowAI plant…') : tr('cases.flowProposal.takeOver', 'Lass FlowAI übernehmen')}
        </button>
      </div>

      {safety.dry_run && (
        <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 text-[11px] text-indigo-900 flex items-center gap-2">
          <span>🧪</span>
          <span><b>{tr('cases.flowProposal.testModeTitle', 'FlowAI im Testmodus')}</b> — {tr('cases.flowProposal.testModeHint', 'es wird nichts gesendet. Freigaben werden gespeichert, aber nicht ausgeführt. Admin kann den Dry-Run in den FlowAI-Sicherheitseinstellungen abschalten.')}</span>
        </div>
      )}

      {error && <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-[11px] text-red-800">{error}</div>}

      {/* #1287 Null-Aktions-Lauf sichtbar: „kein Handlungsbedarf" + Begründung statt Leer-Zustand. */}
      {actions.length === 0 && outcome === 'no_action' && (
        <div className="px-4 py-3 bg-white border-b border-amber-200 text-[12px]">
          <div className="font-medium text-amber-900">✅ {tr('cases.flowProposal.noAction', 'Kein Handlungsbedarf')}</div>
          {summary && <div className="mt-1 text-[12px] whitespace-pre-wrap" style={{ color: 'var(--text-secondary, #4B5563)' }}>{summary}</div>}
        </div>
      )}

      {/* #1287 Fehl-Lauf nie still: klare Meldung statt stummem Button-Reset. */}
      {actions.length === 0 && outcome === 'error' && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-[12px] text-red-800">
          <div className="font-medium">{tr('cases.flowProposal.errorBody', 'FlowAI konnte nicht abschließen — bitte erneut versuchen.')}</div>
          {summary && <div className="mt-1 text-[11px] whitespace-pre-wrap">{summary}</div>}
        </div>
      )}

      {actions.length > 0 && (
        <div className="divide-y divide-amber-200">
          {actions.map((a, idx) => {
            const isOpen = open[a.id];
            const [label, labelCls] = ACTION_LABELS[a.action_type] || [null, null];
            const typeLabel = label ? tr(label, labelCls) : a.action_type;
            const [badgeText, badgeCls] = statusBadge(a);
            const canApprove = isPending(a) && depReady(a) && !isExecuted(a);
            const blocked = isPending(a) && !depReady(a);
            // #1280 freigegeben, aber nicht ausgeführt → „Jetzt ausführen" sobald
            // Dry-Run AUS ist und der Vorgänger ausgeführt wurde.
            const canExecute = isApprovedNotExecuted(a) && depReady(a) && !safety.dry_run;
            return (
              <div key={a.id} className="p-3 text-[12px]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-amber-700">{idx + 1}.</span>
                  <span className="font-medium">{typeLabel}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${badgeCls}`}>{badgeText}</span>
                  {a.depends_on_action_id && (
                    <span className="text-[10px] text-amber-700">↳ {tr('cases.flowProposal.dependsOn', 'nach #')}{a.depends_on_action_id}</span>
                  )}
                  <button onClick={() => setOpen({ ...open, [a.id]: !isOpen })} className="ml-auto text-[11px] text-amber-900 underline">
                    {isOpen ? tr('cases.flowProposal.collapse', 'Einklappen') : tr('cases.flowProposal.show', 'Anzeigen')}
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-2">
                    {editId === a.id ? renderEdit(a) : renderContent(a)}
                    {blocked && (
                      <div className="mt-2 text-[11px] text-amber-700">{tr('cases.flowProposal.blockedHint', 'Erst nach Ausführung des Vorgängers freigebbar.')}</div>
                    )}
                    {canApprove && (
                      <div className="mt-2 flex gap-2">
                        {editId === a.id ? (
                          <>
                            <button onClick={() => saveEdit(a)} disabled={busy} className="px-3 py-1 bg-green-600 text-white rounded text-[12px]">{tr('cases.flowProposal.saveApprove', 'Speichern & Freigeben')}</button>
                            <button onClick={() => setEditId(null)} disabled={busy} className="px-3 py-1 border border-amber-300 rounded text-[12px]">{tr('cases.flowProposal.cancel', 'Abbrechen')}</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => approve(a.id)} disabled={busy} className="px-3 py-1 bg-green-600 text-white rounded text-[12px]">{tr('cases.flowProposal.approve', 'Freigeben')}</button>
                            <button onClick={() => startEdit(a)} disabled={busy} className="px-3 py-1 border border-amber-300 rounded text-[12px]">{tr('cases.flowProposal.edit', 'Bearbeiten')}</button>
                            <button onClick={() => reject(a.id)} disabled={busy} className="ml-auto px-3 py-1 bg-red-600 text-white rounded text-[12px]">{tr('cases.flowProposal.reject', 'Ablehnen')}</button>
                          </>
                        )}
                      </div>
                    )}
                    {canExecute && (
                      <div className="mt-2 flex gap-2 items-center">
                        <button onClick={() => executeNow(a.id)} disabled={busy} className="px-3 py-1 bg-[#338edc] text-white rounded text-[12px]">{tr('cases.flowProposal.executeNow', 'Jetzt ausführen')}</button>
                        <span className="text-[11px] text-amber-700">{tr('cases.flowProposal.approvedAwaitExec', 'Freigegeben — wartet auf Ausführung.')}</span>
                      </div>
                    )}
                    {isApprovedNotExecuted(a) && safety.dry_run && (
                      <div className="mt-2 text-[11px] text-indigo-700">{tr('cases.flowProposal.dryRunWaiting', 'Freigegeben, aber im Testmodus nicht ausgeführt. Nach Abschalten des Dry-Run ausführbar.')}</div>
                    )}
                    {a.execution_status === 'failed' && a.execution_result?.error && (
                      <div className="mt-2 text-[11px] text-red-700">{a.execution_result.error}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
